import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { Search, SlidersHorizontal, X } from "lucide-react";
import PropertyCard from "@/components/PropertyCard";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const propertyTypes = [
  { value: "all", label: "Tous les types" },
  { value: "appartement", label: "Appartement" },
  { value: "maison", label: "Maison" },
  { value: "villa", label: "Villa" },
  { value: "terrain", label: "Terrain" },
  { value: "commercial", label: "Commercial" },
];

const transactionTypes = [
  { value: "all", label: "Toutes transactions" },
  { value: "vente", label: "Vente" },
  { value: "location", label: "Location" },
  { value: "programme", label: "Programme neuf" },
];

const priceRanges = [
  { value: "all", label: "Tous les prix" },
  { value: "0-500000", label: "Jusqu'à CHF 500'000" },
  { value: "500000-1000000", label: "CHF 500'000 - 1'000'000" },
  { value: "1000000-2000000", label: "CHF 1'000'000 - 2'000'000" },
  { value: "2000000-99999999", label: "Plus de CHF 2'000'000" },
];

export default function PropertiesPage() {
  const [searchParams] = useSearchParams();
  const [properties, setProperties] = useState([]);
  const [allProperties, setAllProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [dynamicLocations, setDynamicLocations] = useState([]);

  const [filters, setFilters] = useState({
    search: searchParams.get("search") || "",
    property_type: "all",
    transaction_type: "all",
    price_range: "all",
    location: "all",
  });

  // Build dynamic locations from all properties
  useEffect(() => {
    if (allProperties.length > 0) {
      const locs = [...new Set(allProperties.map(p => p.location).filter(Boolean))].sort();
      setDynamicLocations(locs);
    }
  }, [allProperties]);

  const fetchProperties = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.search) params.search = filters.search;
      if (filters.property_type !== "all") params.property_type = filters.property_type;
      if (filters.transaction_type !== "all") params.transaction_type = filters.transaction_type;
      if (filters.location !== "all") params.location = filters.location;
      if (filters.price_range !== "all") {
        const [min, max] = filters.price_range.split("-");
        params.min_price = parseInt(min);
        params.max_price = parseInt(max);
      }
      const res = await axios.get(`${API}/properties`, { params });
      setProperties(res.data);
      // Fetch all properties once for dynamic locations
      if (allProperties.length === 0) {
        const allRes = await axios.get(`${API}/properties`);
        setAllProperties(allRes.data);
      }
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchProperties();
  }, [filters]); // eslint-disable-line react-hooks/exhaustive-deps

  const activeFilters = Object.entries(filters).filter(
    ([key, val]) => val && val !== "all" && key !== "search"
  ).length;

  const clearFilters = () => {
    setFilters({
      search: "",
      property_type: "all",
      transaction_type: "all",
      price_range: "all",
      location: "all",
    });
  };

  return (
    <div data-testid="properties-page" className="pt-20">
      {/* Header */}
      <div className="bg-[#F5F5F5] py-16 lg:py-24 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <p className="text-[11px] uppercase tracking-[0.2em] text-[#699ab6] font-medium mb-3">
            Nos biens
          </p>
          <h1 className="font-heading text-4xl sm:text-5xl font-medium text-[#333333] tracking-tight mb-8">
            Biens immobiliers
          </h1>

          <div className="flex flex-col sm:flex-row gap-3 max-w-3xl">
            <div className="flex-1 relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#999]" />
              <input
                data-testid="properties-search"
                type="text"
                placeholder="Rechercher par lieu, type..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                className="w-full bg-white pl-12 pr-4 py-3.5 text-sm text-[#333] placeholder:text-[#999] focus:outline-none border border-[#E5E5E5]"
              />
            </div>
            <button
              data-testid="toggle-filters-btn"
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center justify-center gap-2 bg-[#699ab6] text-white px-6 py-3.5 text-[11px] uppercase tracking-[0.15em] font-medium hover:bg-[#5a8ba5] transition-colors"
            >
              <SlidersHorizontal className="w-4 h-4" />
              Filtres
              {activeFilters > 0 && (
                <span className="bg-white text-[#699ab6] text-[10px] w-5 h-5 rounded-full flex items-center justify-center font-bold">
                  {activeFilters}
                </span>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Filters panel */}
      {showFilters && (
        <div data-testid="filters-panel" className="bg-white border-b border-[#E5E5E5] py-8 px-6 lg:px-12">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-[11px] uppercase tracking-[0.15em] text-[#777] font-medium mb-2">
                  Type de bien
                </label>
                <Select
                  value={filters.property_type}
                  onValueChange={(val) => setFilters({ ...filters, property_type: val })}
                >
                  <SelectTrigger data-testid="filter-property-type" className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {propertyTypes.map((t) => (
                      <SelectItem key={t.value} value={t.value}>
                        {t.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="block text-[11px] uppercase tracking-[0.15em] text-[#777] font-medium mb-2">
                  Transaction
                </label>
                <Select
                  value={filters.transaction_type}
                  onValueChange={(val) => setFilters({ ...filters, transaction_type: val })}
                >
                  <SelectTrigger data-testid="filter-transaction-type" className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {transactionTypes.map((t) => (
                      <SelectItem key={t.value} value={t.value}>
                        {t.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="block text-[11px] uppercase tracking-[0.15em] text-[#777] font-medium mb-2">
                  Budget
                </label>
                <Select
                  value={filters.price_range}
                  onValueChange={(val) => setFilters({ ...filters, price_range: val })}
                >
                  <SelectTrigger data-testid="filter-price" className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {priceRanges.map((t) => (
                      <SelectItem key={t.value} value={t.value}>
                        {t.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="block text-[11px] uppercase tracking-[0.15em] text-[#777] font-medium mb-2">
                  Commune
                </label>
                <Select
                  value={filters.location}
                  onValueChange={(val) => setFilters({ ...filters, location: val })}
                >
                  <SelectTrigger data-testid="filter-location" className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Toutes les communes</SelectItem>
                    {dynamicLocations.map((loc) => (
                      <SelectItem key={loc} value={loc}>
                        {loc}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {activeFilters > 0 && (
              <button
                data-testid="clear-filters-btn"
                onClick={clearFilters}
                className="mt-4 flex items-center gap-1 text-xs text-[#699ab6] hover:text-[#333] transition-colors"
              >
                <X className="w-3 h-3" />
                Réinitialiser les filtres
              </button>
            )}
          </div>
        </div>
      )}

      {/* Properties Grid */}
      <div className="py-12 lg:py-20 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <p className="text-sm text-[#777] mb-8">
            {properties.length} bien{properties.length !== 1 ? "s" : ""} trouvé{properties.length !== 1 ? "s" : ""}
          </p>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-10">
              {[1, 2, 3].map((i) => (
                <div key={i} className="animate-pulse">
                  <div className="aspect-square bg-[#F0F0F0] mb-4" />
                  <div className="h-3 bg-[#F0F0F0] w-1/3 mb-2" />
                  <div className="h-5 bg-[#F0F0F0] w-3/4 mb-2" />
                  <div className="h-4 bg-[#F0F0F0] w-1/2" />
                </div>
              ))}
            </div>
          ) : properties.length === 0 ? (
            <div data-testid="no-results" className="text-center py-20">
              <p className="font-heading text-2xl text-[#333333] mb-3">
                Aucun bien trouvé
              </p>
              <p className="text-[#777] text-sm font-light">
                Essayez de modifier vos critères de recherche
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-10">
              {properties.map((property, i) => (
                <PropertyCard key={property.id} property={property} index={i} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
